import { useEffect, useState } from "react";
import { DialogProps } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";
import { getReportMonth, setReportMonth } from "../../utils/reportDialogState";

/** Resource ids for the month option labels, in calendar order. */
const MONTH_IDS = [
  "TIGER_REPORT_JANUARY",
  "TIGER_REPORT_FEBRUARY",
  "TIGER_REPORT_MARCH",
  "TIGER_REPORT_APRIL",
  "TIGER_REPORT_MAY",
  "TIGER_REPORT_JUNE",
  "TIGER_REPORT_JULY",
  "TIGER_REPORT_AUGUST",
  "TIGER_REPORT_SEPTEMBER",
  "TIGER_REPORT_OCTOBER",
  "TIGER_REPORT_NOVEMBER",
  "TIGER_REPORT_DECEMBER",
];

// Port of report_month_shell_c (IDD_REPORT_MONTH_SHELL): pick the month and
// year a MONTH-model report covers. Themed to match the Graph Channel
// Selection dialog.
export interface ReportMonthDialogProps extends DialogProps {
  /** month is 1-12. */
  onOk?: (month: number, year: number) => void;
  onCancel?: () => void;
}

export function ReportMonthDialog({
  open,
  onClose,
  onApply,
  onOk,
  onCancel,
}: ReportMonthDialogProps) {
  const [month, setMonth] = useState(1);
  const [year, setYear] = useState("");
  const [error, setError] = useState("");

  // Seed from the last-used month (C++ g_report_month_date), else now.
  useEffect(() => {
    if (!open) return;
    const last = getReportMonth();
    setMonth(last.month);
    setYear(String(last.year));
    setError("");
  }, [open]);

  const handleOk = () => {
    const y = Number(year);
    if (!/^\d{4}$/.test(year) || y < 1901) {
      setError(getTigerResource("TIGER_REPORT_YEAR_INVALID"));
      return;
    }
    setError("");
    setReportMonth(month, y);
    onOk?.(month, y);
    onApply?.({ month, year: y });
    onClose();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose();
  };

  if (!open) return null;
  const title = getTigerResource("TIGER_REPORT_MONTH_TITLE");

  return (
    <div style={s.overlay}>
      <div
        style={{
          ...s.dialog,
          width: 400,
          height: "auto",
          minHeight: 0,
          resize: "none",
          top: "calc(50vh - 140px)",
          left: "max(2.5vw, calc(50vw - 200px))",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={s.header}>
          <span style={s.title}>{title}</span>
          <button style={s.closeBtn} onClick={handleCancel} aria-label={getTigerResource("TIGER_RES_CLOSE")}>
            &#x2715;
          </button>
        </div>

        <div style={s.body}>
          <p style={{ margin: 0, fontSize: 13, color: "#374151" }}>
            {getTigerResource("TIGER_REPORT_MONTH_LABEL")}
          </p>
          <div style={s.card}>
            <div style={s.fieldGroup}>
              <div style={s.field}>
                <label style={s.fieldLabel} htmlFor="report-month">
                  {getTigerResource("TIGER_REPORT_MONTH")}
                </label>
                <select
                  id="report-month"
                  style={s.input}
                  aria-label={getTigerResource("TIGER_REPORT_MONTH")}
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_IDS.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {getTigerResource(m)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ ...s.field, flex: "0 0 100px" }}>
                <label style={s.fieldLabel} htmlFor="report-month-year">
                  {getTigerResource("TIGER_REPORT_YEAR")}
                </label>
                <input
                  id="report-month-year"
                  style={s.input}
                  type="text"
                  aria-label={getTigerResource("TIGER_REPORT_YEAR")}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  maxLength={4}
                />
              </div>
            </div>
          </div>
          {error && (
            <div role="alert" style={{ color: "#b00020", fontSize: 12 }}>
              {error}
            </div>
          )}
        </div>

        <div style={s.footer}>
          <button style={s.btnPrimary} onClick={handleOk}>
            {getTigerResource("TIGER_RES_OK")}
          </button>
          <button style={s.btnSecondary} onClick={handleCancel}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
}
